import { useCallback, useState } from 'react'
import { dateValidatorByFormat } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';

import { PlansCoveragesDates } from './usePlanCoveragesDates';

const INVALID_PLAN_COVERAGE_DATE_TEXT = 'Coverage end date and last date to submit claims cannot be before plan started';
const CLAIM_LAST_DATE_ERROR_TEXT = 'Last date to submit claims cannot be before coverage end date';

const dateValidator = (dateString?: string) => {
  if (!dateString) return false;
  if (dateValidatorByFormat(dateString, DEFAULT_DATE_FORMAT)) return false;
  return true;
};

export interface PlansCoveragesDatesErrors {
  planId: string;
  coverageEndDateError?: boolean;
  lastDayToSubmitClaimError?: boolean;
  isLastDateToSubmitBeforeEndOfCoverageError?: boolean;
  invalidPlanCoverageDateErrorText?: string
  claimLastDateErrorText?: string;
}

export const usePlansCoveragesDatesValidation = () => {
  const [planCoveragesErrors, setPlanCoveragesErrors] = useState<PlansCoveragesDatesErrors[]>([]);

  const getPlanCoveragesClaimDateError = useCallback((planId: string) => {
    const planCoverage = planCoveragesErrors.find((item) => item.planId === planId);
    const lastDayToSubmitClaimError = !!planCoverage?.lastDayToSubmitClaimError;
    const isLastDateToSubmitBeforeEndOfCoverageError = !!planCoverage?.isLastDateToSubmitBeforeEndOfCoverageError;
    return lastDayToSubmitClaimError || isLastDateToSubmitBeforeEndOfCoverageError;
  }, [planCoveragesErrors]);

  const getPlanCoveragesEndDateError = useCallback((planId: string) => {
    const planCoverage = planCoveragesErrors.find((item) => item.planId === planId);
    return !!planCoverage?.coverageEndDateError;
  }, [planCoveragesErrors]);

  const getInvalidPlanCoverageDateErrorText = useCallback((planId: string) => {
    const planCoverage = planCoveragesErrors.find((item) => item.planId === planId);
    return !!planCoverage?.invalidPlanCoverageDateErrorText;
  }, [planCoveragesErrors]);

  const coverageErrorsParser = useCallback((
    plansCoveragesDates: PlansCoveragesDates[],
    currentValidFromDates: Record<string, string>,
  ) => {
    const errors: PlansCoveragesDatesErrors[] = plansCoveragesDates.map((item) => {
      const currentPermanentLastDate = currentValidFromDates[item.planId];
      const coverageEndDateError = !item?.coverageEndDate
        || dateValidator(item?.coverageEndDate)
        || dayjs(item?.coverageEndDate).isBefore(dayjs(currentPermanentLastDate));

      const lastDayToSubmitClaimError = !item?.lastDayToSubmitClaim
        || dateValidator(item?.lastDayToSubmitClaim)
        || dayjs(item?.lastDayToSubmitClaim).isBefore(dayjs(currentPermanentLastDate));

      const invalidPlanCoverageDateErrorText = (dayjs(item?.coverageEndDate).isBefore(dayjs(currentPermanentLastDate))
        || (dayjs(item?.lastDayToSubmitClaim).isBefore(dayjs(currentPermanentLastDate))
        && !getInvalidPlanCoverageDateErrorText(item.planId)));

      const isLastDateToSubmitBeforeEndOfCoverageError = dayjs(item?.lastDayToSubmitClaim).isBefore(dayjs(item?.coverageEndDate));
      return {
        planId: item.planId,
        coverageEndDateError,
        lastDayToSubmitClaimError,
        isLastDateToSubmitBeforeEndOfCoverageError,
        invalidPlanCoverageDateErrorText: invalidPlanCoverageDateErrorText ? INVALID_PLAN_COVERAGE_DATE_TEXT : undefined,
        claimLastDateErrorText: isLastDateToSubmitBeforeEndOfCoverageError ? CLAIM_LAST_DATE_ERROR_TEXT : undefined,
      };
    });
    setPlanCoveragesErrors(errors);
    const hasErrors = errors.some((error) =>
      error.coverageEndDateError
      || error.lastDayToSubmitClaimError
      || error.isLastDateToSubmitBeforeEndOfCoverageError
      || error.invalidPlanCoverageDateErrorText
      || error.claimLastDateErrorText,
    );
    return hasErrors;
  }, [getInvalidPlanCoverageDateErrorText]);

  return {
    planCoveragesErrors,
    setPlanCoveragesErrors,
    getPlanCoveragesClaimDateError,
    getPlanCoveragesEndDateError,
    coverageErrorsParser,
  }
}
